import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  IconButton,
  Select,
  useDisclosure,
  useToast,
  Flex,
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import axiosInstance from '../../../utils/axiosInstance';
import axios from 'axios';
import { format, parse } from 'date-fns';

// Generate 30-minute interval times
const generateTimes = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    const hourString = hour.toString().padStart(2, '0');
    times.push(`${hourString}:00`);
    times.push(`${hourString}:30`);
  }
  return times;
};

const availableTimes = generateTimes(); // 30-minute interval times

// Default availability - photographer is always available by default
const defaultAvailability = [
  { day: 'Monday', start_time: '09:00', end_time: '17:00', available: true },
  { day: 'Tuesday', start_time: '09:00', end_time: '17:00', available: true },
  { day: 'Wednesday', start_time: '09:00', end_time: '17:00', available: true },
  { day: 'Thursday', start_time: '09:00', end_time: '17:00', available: true },
  { day: 'Friday', start_time: '09:00', end_time: '17:00', available: true },
  { day: 'Saturday', start_time: '09:00', end_time: '17:00', available: true },
  { day: 'Sunday', start_time: '09:00', end_time: '17:00', available: true },
];

export default function PhotographerAvailability({ photographerId, currentUser, isEditable }) {
  const [availability, setAvailability] = useState(defaultAvailability); // State for availability
  const [isEditing, setIsEditing] = useState(false); // State to track editing mode
  const [editedAvailability, setEditedAvailability] = useState(defaultAvailability); // Edited availability state
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const backendUrl = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_BACKEND_URL
  : 'http://localhost:8080';
  // Fetch availability data from the backend
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await axios.get(`${backendUrl}/photographer/${photographerId}/availability`);
        if (response.data.availability && response.data.availability.length > 0) {
          setAvailability(response.data.availability);
          setEditedAvailability(response.data.availability);
        }
      } catch (error) {
        console.error('Failed to fetch availability:', error);
      }
    };

    fetchAvailability();
  }, [photographerId]);

  // Toggle editing mode
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Save the updated availability
  const handleSaveClick = async () => {
    // Validate that start time is before end time for all available days
    const invalidDay = editedAvailability.find(
      (slot) =>
        slot.available &&
        availableTimes.indexOf(slot.start_time) >= availableTimes.indexOf(slot.end_time)
    );

    if (invalidDay) {
      toast({
        title: `Invalid Time Selection`,
        description: `Start time must be before end time on ${invalidDay.day}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await axiosInstance.post(`${backendUrl}/photographer/set-availability`, {
        availability: editedAvailability,
      });
      setAvailability(editedAvailability); // Save the edited availability
      setIsEditing(false); // Exit editing mode
      toast({
        title: 'Availability updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to update availability:', error);
      toast({
        title: 'Failed to update availability',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Cancel editing and revert to original values
  const handleCancelClick = () => {
    setEditedAvailability(availability); // Revert to original values
    setIsEditing(false);
  };

  // Update availability values during editing
  const handleAvailabilityChange = (index, field, value) => {
    const updated = [...editedAvailability];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAvailability(updated);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold">
          Availability Schedule
        </Text>
        {/* Only show the edit icon if the photographer is the owner */}
        {isEditable && !isEditing && (
          <IconButton
            icon={<EditIcon />}
            onClick={handleEditClick}
            aria-label="Edit Availability"
          />
        )}
      </Flex>

      <VStack spacing={4} align="start">
        {availability.map((slot, index) => (
          <HStack key={index} spacing={4} w="full" align="center">
            {/* Day */}
            <Text w="30%">{slot.day}</Text>

            {/* Availability Toggle */}
            {isEditing ? (
              <Select
                value={editedAvailability[index].available ? 'available' : 'unavailable'}
                onChange={(e) =>
                  handleAvailabilityChange(index, 'available', e.target.value === 'available')
                }
                size="sm"
                w="30%"
              >
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </Select>
            ) : (
              <Text w="30%">
                {slot.available ? 'Available' : 'Unavailable'}
              </Text>
            )}

            {/* Start Time */}
            {isEditing && editedAvailability[index].available ? (
              <Select
                value={editedAvailability[index].start_time}
                onChange={(e) => handleAvailabilityChange(index, 'start_time', e.target.value)}
                size="sm"
                w="30%"
              >
                {availableTimes.map((time) => (
                  <option key={time} value={time}>
                    {format(parse(time, 'HH:mm', new Date()), 'hh:mm a')}
                  </option>
                ))}
              </Select>
            ) : slot.available ? (
              <Text w="30%">{format(parse(slot.start_time, 'HH:mm', new Date()), 'hh:mm a')}</Text>
            ) : null}

            {/* End Time */}
            {isEditing && editedAvailability[index].available ? (
              <Select
                value={editedAvailability[index].end_time}
                onChange={(e) => handleAvailabilityChange(index, 'end_time', e.target.value)}
                size="sm"
                w="30%"
              >
                {availableTimes.map((time) => (
                  <option key={time} value={time}>
                    {format(parse(time, 'HH:mm', new Date()), 'hh:mm a')}
                  </option>
                ))}
              </Select>
            ) : slot.available ? (
              <Text w="30%">{format(parse(slot.end_time, 'HH:mm', new Date()), 'hh:mm a')}</Text>
            ) : null}
          </HStack>
        ))}

        {/* Show Save/Cancel buttons only when in editing mode */}
        {isEditable && isEditing && (
          <HStack spacing={4}>
            <Button colorScheme="green" onClick={handleSaveClick}>
              Save
            </Button>
            <Button colorScheme="red" onClick={handleCancelClick}>
              Cancel
            </Button>
          </HStack>
        )}
      </VStack>
    </Box>
  );
}
